/**
 * Which session an activity card shows (docs/prd-homepage-redesign.md §3.5).
 *
 * With a time window, the earliest session starting inside it that has not ended.
 * Otherwise the earliest session that has not ended, falling back to the last past
 * one so a card still shows a real date. Shared by the homepage API, All Activities
 * and the card transform so they always agree. A session without an end time counts
 * as open until it starts.
 */
export interface SessionWindow {
  /** Epoch ms, inclusive. */
  start?: number;
  /** Epoch ms, inclusive. */
  end?: number;
}

export function pickDisplaySession<
  T extends { startTime: string | Date; endTime?: string | Date | null },
>(sessions: T[], window: SessionWindow | undefined, now: number): T | undefined {
  if (sessions.length === 0) return undefined;
  const at = (s: T) => new Date(s.startTime).getTime();
  const open = (s: T) => (s.endTime ? new Date(s.endTime).getTime() : at(s)) > now;
  const sorted = [...sessions].sort((a, b) => at(a) - at(b));
  if (window && (window.start !== undefined || window.end !== undefined)) {
    const inWindow = sorted.find((s) => {
      const t = at(s);
      if (window.start !== undefined && t < window.start) return false;
      if (window.end !== undefined && t > window.end) return false;
      return open(s);
    });
    if (inWindow) return inWindow;
  }
  return sorted.find(open) ?? sorted[sorted.length - 1];
}
